import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { Formik, Field } from 'formik';
import {
  FieldCustom, FieldToggle, FieldSelect, FieldTextarea
} from 'components/molecules';
import {
  Form, Button, Title, Spacer
} from 'components/atoms';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { generateSubwebPath } from 'services/generators';
import {
  getSubwebTypes, SUBWEB_TYPE_KEY, queryNewsChannel
} from 'services/requests'
import { useQuery } from 'react-query'
import { slugRegex } from 'services/constants';
import { Authenticator, Toast } from 'containers'
import { toast } from 'react-toastify';

export const Schema = Yup.object().shape({
  description: Yup.string().required('Campo obrigatório'),
  namePt: Yup.string().required('Campo obrigatório')
    .min(1, 'Mínimo de 1 carácter')
    .max(250, 'Máximo de 250 caracteres')
    .required('Campo obrigatório'),
  nameEn: Yup.string().required('Campo obrigatório')
    .min(1, 'Mínimo de 1 carácter')
    .max(250, 'Máximo de 250 caracteres')
    .required('Campo obrigatório'),
  active: Yup.boolean(),
  slug: Yup.string().matches(slugRegex, 'Apenas pode ter letras minúsculas, palavras separadas por -, e não pode conter a palavra noticias ou news').required('Campo obrigatório'),
  typeId: Yup.string().required('Campo obrigatório'),
  uaOnlineId: Yup.string().nullable(),
  serviceGuruId: Yup.string().nullable(),
  serviceRHid: Yup.string().nullable(),
  analyticsId: Yup.string().nullable(),
  initials: Yup.string()
    .min(1, 'Mínimo de 1 carácter')
    .max(5, 'Máximo de 5 caracteres')
    .required('Campo obrigatório'),
});

const logoBottomSchema = Yup.object().shape({
  logoPtBottom: Yup.string().url('Deve ser um url da imagem').required('Campo obrigatório'),
  logoEnBottom: Yup.string().url('Deve ser um url da imagem').required('Campo obrigatório'),
})

const logoTopSchema = Yup.object().shape({
  logoPtTop: Yup.string().url('Deve ser um url da imagem').required('Campo obrigatório'),
  logoEnTop: Yup.string().url('Deve ser um url da imagem').required('Campo obrigatório'),
})


const SubwebCreateForm = ({
  initialValues,
  onSubmit,
  onDiscard,
  isLoading,
  showReset,
  isEdit = false,
}) => {
  const [numberImageInputs, setNumberImageInputs] = React.useState(0)

  const computedSchema = React.useMemo(() => {
    const schemaWithBottom = Schema.concat(logoBottomSchema)
    const schemaWithBoth = schemaWithBottom.concat(logoTopSchema)

    // requires bottom logos
    if (numberImageInputs === 1) {
      return schemaWithBottom
    }
    if (numberImageInputs === 2) {
      return schemaWithBoth
    }

    return Schema
  }, [numberImageInputs])

  const isInitialValid = useMemo(
    () => Schema.isValidSync(initialValues),
    [initialValues]
  );

  const {
    isLoading: isLoadingSubwebType,
    data: dataSubwebTypes
  } = useQuery([SUBWEB_TYPE_KEY], getSubwebTypes)

  const computeOptions = (opts, valueKey = 'id', labelKey = 'name') => opts.map((option) => ({
    value: option[valueKey],
    label: option[labelKey],
    original: option
  }))

  const [isLoadingNewsType, setLoading] = useState(true)
  const [newsTypesOptions, setChannel] = useState([]);


  useEffect(() => {
    const getNewsData = async () => {
      try {
        setLoading(true)
        const { data: { data: channelData } } = await queryNewsChannel({});

        setChannel(computeOptions(channelData));

        setLoading(false)
      } catch (err) {
        setLoading(false)
        toast.error(
          <Toast
            type="error"
            title="Erro a carregar formulário de Notícias"
            description={err.message}
          />
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
    getNewsData();
  }, []);

  const subwebTypesOptions = useMemo(() => {
    if (dataSubwebTypes && dataSubwebTypes.data) {
      return dataSubwebTypes.data.map(type => ({
        label: type.name,
        value: type.id,
        original: type,
      }))
    }
    return []
  }, [dataSubwebTypes])

  const renderImageFields = (value, options) => {
    const selected = options.find(opt => (opt.value ? opt.value === value : null))
    const numberLogos = selected && selected.original && selected.original.numberLogos
      ? selected.original.numberLogos
      : 0
    setNumberImageInputs(numberLogos)

    if (numberLogos === 1) {
      return (
        <>
          <Field
            title="Designação PT"
            name="logoPtBottom"
            component={FieldCustom}
            info="Imagem por baixo do logótipo em português"
          />
          <Field
            title="Designação EN"
            name="logoEnBottom"
            component={FieldCustom}
            info="Imagem por baixo do logótipo em inglês"
          />
        </>
      )
    }

    if (numberLogos === 2) {
      return (
        <>
          <Field
            title="Logotipo PT"
            name="logoPtTop"
            component={FieldCustom}
          />
          <Field
            title="Logotipo EN"
            name="logoEnTop"
            component={FieldCustom}
          />
          <Field
            title="Designação PT"
            name="logoPtBottom"
            component={FieldCustom}
            info="Imagem por baixo do logótipo em português"
          />
          <Field
            title="Designação EN"
            name="logoEnBottom"
            component={FieldCustom}
            info="Imagem por baixo do logótipo em inglês"
          />
        </>
      )
    }

    return null
  }

  return (
    <Formik
      initialValues={initialValues}
      isInitialValid={isInitialValid}
      validationSchema={computedSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        isSubmitting, dirty, values, resetForm,
      }) => (
        <Form modifiers={['shrink']}>
          <Title as="h3" modifiers={['paddingBottomM']}>Informação base</Title>
          <Field
            title="Subweb online"
            name="active"
            component={FieldToggle}
            info={values.active ? 'online' : 'offline'}
            modifiers={['stretch']}
          />
          <Field
            title="Título PT"
            name="namePt"
            info="Título em português"
            component={FieldCustom}
            maxLetters={250}
          />
          <Field
            title="Título EN"
            name="nameEn"
            info="Título em inglês"
            component={FieldCustom}
            maxLetters={250}
          />
          <Field
            title="Descrição"
            name="description"
            info="Descrição da subweb"
            component={FieldTextarea}
          />
          <Field
            title="Endereço"
            name="slug"
            component={FieldCustom}
            info={generateSubwebPath(
              {
                subweb: values.slug ? values.slug : '',
                lang: 'pt',
                withHost: true
              }
            )}
          />

          <Field
            title="Iniciais"
            name="initials"
            component={FieldCustom}
            info="Alternativa ao acrónimo com um limite de 5 caractéres"
            maxLetters={5}
          />

          <Authenticator requires="admin">
            <Spacer top="l" />
            <Title as="h3" modifiers={['paddingBottomM']}>Aspeto</Title>
            <Field
              name="typeId"
              title="Tipo de subweb"
              component={FieldSelect}
              placeholder="Selecione um tipo de subweb..."
              options={subwebTypesOptions}
              isLoading={isLoadingSubwebType}
            />

            {renderImageFields(values.typeId, subwebTypesOptions)}
          </Authenticator>

          <Spacer top="l" />

          <Title as="h3" modifiers={['paddingBottomM']}>Ligação a outros serviços</Title>
          <Field
            title="ID Rh"
            name="serviceRHid"
            component={FieldCustom}
          />

          <Field
            title="ID Guru"
            name="serviceGuruId"
            component={FieldCustom}
          />

          <Field
            title="ID notícias UA"
            name="uaOnlineId"
            component={FieldSelect}
            options={newsTypesOptions}
            isLoading={isLoadingNewsType}
          />

          <Field
            title="ID Analytics"
            name="analyticsId"
            component={FieldCustom}
          />

          <Actions>
            {showReset && (
              <Button
                type="button"
                modifiers={['marginRight', 'capitalize', dirty ? '' : 'disabled']}
                onClick={() => resetForm(initialValues)}
              >
            Repor
              </Button>
            )}
            {typeof onDiscard === 'function' && (
              <Button
                type="button"
                modifiers={['marginRight', 'capitalize', dirty ? '' : 'disabled']}
                onClick={onDiscard}
              >
                Cancelar
              </Button>
            )}
            <Button
              type="submit"
              modifiers={['dark', 'capitalize', dirty ? '' : 'disabled']}
              loading={isLoading}
            >
              {isEdit ? 'Editar subweb' : 'Criar Subweb'}
            </Button>
          </Actions>
        </Form>
      )}
    </Formik>
  );
};

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing.l};
`

SubwebCreateForm.propTypes = {
  onSubmit: PropTypes.func,
  onDiscard: PropTypes.func,
  initialValues: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  showReset: PropTypes.bool,
};

SubwebCreateForm.defaultProps = {
  onSubmit: () => null,
  onDiscard: undefined,
  initialValues: {},
  isLoading: false,
  showReset: false,
};

export default SubwebCreateForm;
